import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Flex, Grid, Text, Heading, Spinner, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader,
  DrawerBody, Button, IconButton, Table, Thead, Tbody, Tr, Th, Td, useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi';
import {
  getHistoricalStudent, updateHistoricalStudent, createHistoricalGrade, updateHistoricalGrade,
  createHistoricalPayment, updateHistoricalPayment, deleteHistoricalStudent, SOURCE_LABEL, SOURCE_TONE, STATUS, SOURCE_OF_RECORD,
  formatScore, formatMoney, formatDate,
} from 'api/administrador/historicalBase';
import { StatusPill } from 'components/monitoring/ai/common';
import RecordModal from './RecordModal';

function Section({ title, action, children }) {
  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2.5} gap={3}>
        <Text fontSize="12px" fontWeight="600" letterSpacing=".06em" textTransform="uppercase" color="gray.500">{title}</Text>
        {action}
      </Flex>
      {children}
    </Box>
  );
}

function Field({ label, children }) {
  return (
    <Box minW={0}>
      <Text fontSize="12px" color="gray.500">{label}</Text>
      <Text fontSize="14px" color="gray.800" wordBreak="break-word">{children || '—'}</Text>
    </Box>
  );
}

function SourceOfRecord({ value }) {
  const s = SOURCE_OF_RECORD[value] || SOURCE_OF_RECORD.archivo;
  return <Text as="span" fontSize="12px" fontWeight="600" color={s.color} whiteSpace="nowrap">{s.label}</Text>;
}

const thProps = { fontSize: '11px', color: 'gray.500', px: 3, py: 2, bg: 'gray.50', textTransform: 'uppercase' };
const tdProps = { fontSize: '13.5px', px: 3, py: 2 };

export default function StudentDrawer({ studentId, isOpen, onClose, canEdit, options, onChanged, onDeleted }) {
  const toast = useToast();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      setStudent(await getHistoricalStudent(studentId));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[base histórica] error cargando la ficha:', e);
      setStudent(null);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    if (isOpen) load();
    else setStudent(null);
  }, [isOpen, load]);

  const done = async (message) => {
    toast({ status: 'success', title: message, duration: 3000, isClosable: true });
    await load();
    if (onChanged) onChanged();
  };

  const opts = options || {};
  const statusOptions = (opts.statuses || Object.keys(STATUS)).map((s) => ({ value: s, label: STATUS[s]?.label || s }));

  const editStudent = () => setModal({
    title: 'Editar datos',
    fields: [
      { name: 'firstNames', label: 'Nombres' },
      { name: 'lastNames', label: 'Apellidos' },
      { name: 'documentType', label: 'Tipo de documento', type: 'select', options: withCurrent(opts.documentTypes, student.documentType), allowEmpty: !student.documentType },
      { name: 'document', label: 'Número de documento', inputMode: 'numeric' },
      { name: 'email', label: 'Correo', type: 'email', full: true },
      { name: 'phone', label: 'Teléfono', inputMode: 'tel' },
      { name: 'city', label: 'Ciudad' },
      { name: 'birthdate', label: 'Fecha de nacimiento', type: 'date' },
      { name: 'enrollmentDate', label: 'Fecha de matrícula', type: 'date' },
      { name: 'program', label: 'Programa', type: 'select', options: withCurrent(opts.programs, student.program), allowEmpty: true },
      { name: 'status', label: 'Estado', type: 'select', options: statusOptions },
    ],
    initialValues: {
      firstNames: student.firstNames || '',
      lastNames: student.lastNames || '',
      documentType: student.documentType || '',
      document: student.document || '',
      email: student.email || '',
      phone: student.phone || '',
      city: student.city || '',
      birthdate: student.birthdate || '',
      enrollmentDate: student.enrollmentDate || '',
      program: student.program || '',
      status: student.status || 'registrado',
    },
    onSubmit: async (v) => {
      await updateHistoricalStudent(student.id, v);
      await done('Datos guardados');
    },
  });

  const editGrade = (g) => setModal({
    title: g ? 'Editar nota' : 'Agregar nota',
    fields: [
      { name: 'subject', label: 'Materia', type: 'select', options: withCurrent(opts.subjects, g?.subject), allowEmpty: !g, full: true },
      { name: 'program', label: 'Programa', type: 'select', options: withCurrent(opts.programs, g?.program), allowEmpty: true },
      { name: 'score', label: 'Nota (0 a 10)', inputMode: 'decimal', placeholder: 'Ej. 8,5' },
      { readOnly: true, label: 'Original', value: g ? [g.originalSubject, g.originalValue].filter(Boolean).join(' · ') : 'No aplica', full: true },
      { readOnly: true, label: 'Fuente', value: g ? (g.source === 'archivo' ? 'Del archivo → pasará a Editada' : SOURCE_OF_RECORD[g.source]?.label) : 'Agregada (automático)', full: true },
    ],
    initialValues: { subject: g?.subject || '', program: g?.program || student.program || '', score: g ? formatScore(g.score) : '' },
    onSubmit: async (v) => {
      if (g) await updateHistoricalGrade(g.id, v);
      else await createHistoricalGrade(student.id, v);
      await done(g ? `Nota de ${v.subject} guardada` : `Nota de ${v.subject} agregada`);
    },
  });

  const editPayment = (p) => setModal({
    title: p ? 'Editar pago' : 'Agregar pago',
    fields: [
      { name: 'date', label: 'Fecha', type: 'date' },
      { name: 'amount', label: 'Valor (COP)', inputMode: 'numeric', placeholder: 'Ej. 75000' },
      { name: 'concept', label: 'Concepto', placeholder: 'Ej. derechos_grado' },
      { name: 'method', label: 'Método', type: 'select', options: withCurrent(opts.paymentMethods, p?.method), allowEmpty: true },
      { readOnly: true, label: 'Original', value: p?.originalData ? Object.values(p.originalData).filter((x) => x !== null && x !== '').join(' · ') : 'No aplica', full: true },
      { readOnly: true, label: 'Fuente', value: p ? (p.source === 'archivo' ? 'Del archivo → pasará a Editado' : SOURCE_OF_RECORD[p.source]?.label) : 'Agregado (automático)', full: true },
    ],
    initialValues: { date: p?.date || '', amount: p?.amount ?? '', concept: p?.concept || '', method: p?.method || '' },
    onSubmit: async (v) => {
      if (p) await updateHistoricalPayment(p.id, v);
      else await createHistoricalPayment(student.id, v);
      await done(p ? 'Pago guardado' : 'Pago agregado');
    },
  });

  const deleteStudent = () => setModal({
    title: 'Eliminar estudiante',
    description: 'Se quita de la Base histórica junto con sus notas y pagos. No se borra de la base de datos: queda en Auditoría con quién y por qué, y si se vuelve a importar el archivo no reaparece.',
    fields: [{ name: 'reason', label: 'Motivo (opcional)', placeholder: 'Ej. registro sin nombre, posible bot', full: true }],
    initialValues: { reason: '' },
    submitLabel: 'Eliminar',
    danger: true,
    onSubmit: async (v) => {
      await deleteHistoricalStudent(student.id, v);
      toast({ status: 'success', title: 'Estudiante eliminado de la Base histórica', duration: 3000, isClosable: true });
      if (onDeleted) onDeleted();
    },
  });

  const s = student;
  const emptyGrades = s ? s.grades.filter((g) => g.score === null).length : 0;
  const cols = canEdit ? 6 : 5;

  return (
    <>
      <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px" pr={12}>
            {s ? (
              <>
                <Heading fontSize="19px" fontWeight="700">{[s.firstNames, s.lastNames].filter(Boolean).join(' ')}</Heading>
                <Flex gap={2} mt={2} wrap="wrap" align="center">
                  <StatusPill label={`${SOURCE_LABEL[s.source]} · cód. ${s.sourceCode}`} tone={SOURCE_TONE[s.source]} />
                  {s.status && <StatusPill label={STATUS[s.status]?.label || s.status} tone="gray" />}
                  {s.reviewReason && <StatusPill label="Por revisar" tone="orange" />}
                </Flex>
                {s.reviewReason && (
                  <Text fontSize="13px" fontWeight="400" color="orange.600" mt={2}>{s.reviewReason}</Text>
                )}
              </>
            ) : <Text fontSize="16px">Ficha del estudiante</Text>}
          </DrawerHeader>
          <DrawerBody py={5}>
            {loading && !s && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
            {!loading && !s && <Text color="gray.500" fontSize="sm">No se pudo cargar la ficha. Cierra e intenta de nuevo.</Text>}
            {s && (
              <Flex direction="column" gap={7}>
                <Section
                  title="Datos"
                  action={canEdit
                    ? <Button size="sm" variant="outline" leftIcon={<FiEdit2 />} color="#574feb" onClick={editStudent}>Editar</Button>
                    : <Text fontSize="12px" color="gray.500">Solo consulta</Text>}
                >
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }} gap={3}>
                    <Field label="Documento">{[s.documentType, s.document].filter(Boolean).join(' ')}</Field>
                    <Field label="Fecha de nacimiento">{s.birthdate ? formatDate(s.birthdate) : null}</Field>
                    <Field label="Correo">
                      {s.email}
                      {s.email && (
                        <Text as="span" display="block" fontSize="12px" fontWeight="600" color={s.emailStatus === 'dudoso' ? 'orange.500' : 'green.600'}>
                          {s.emailStatus === 'dudoso' ? 'Dudoso' : 'Válido'}
                        </Text>
                      )}
                    </Field>
                    <Field label="Teléfono">{s.phone}</Field>
                    <Field label="Ciudad">{s.city}</Field>
                    <Field label="Fecha de matrícula">{s.enrollmentDate ? formatDate(s.enrollmentDate) : null}</Field>
                    <Field label="Programa">{s.program}</Field>
                    <Field label="Estado">{STATUS[s.status]?.label}</Field>
                  </Grid>
                  {s.editedBy && <Text fontSize="12px" color="gray.400" mt={2}>Editado por {s.editedBy} el {formatDate(s.editedAt)}</Text>}
                </Section>

                <Section
                  title={`Notas${s.grades.length ? ` · ${emptyGrades ? `${emptyGrades} vacías` : 'completas'}` : ''}`}
                  action={canEdit && <Button size="sm" variant="outline" leftIcon={<FiPlus />} color="#574feb" onClick={() => editGrade(null)}>Agregar nota</Button>}
                >
                  <Box border="1px solid" borderColor="gray.200" borderRadius="10px" overflowX="auto">
                    <Table size="sm">
                      <Thead><Tr><Th {...thProps}>Materia</Th><Th {...thProps}>Programa</Th><Th {...thProps}>Original</Th><Th {...thProps}>Nota</Th><Th {...thProps}>Fuente</Th>{canEdit && <Th {...thProps} />}</Tr></Thead>
                      <Tbody>
                        {s.grades.length === 0 && (
                          <Tr><Td colSpan={cols} {...tdProps} color="gray.500" py={4}>El archivo de origen no trae notas.{canEdit ? ' Agrega la primera con «Agregar nota».' : ''}</Td></Tr>
                        )}
                        {s.grades.map((g) => (
                          <Tr key={g.id}>
                            <Td {...tdProps}>{g.subject}</Td>
                            <Td {...tdProps}>{g.program || '—'}</Td>
                            <Td {...tdProps} fontFamily="mono" fontSize="12px" color="gray.500">{[g.originalSubject, g.originalValue].filter(Boolean).join(' · ') || '—'}</Td>
                            <Td {...tdProps} fontWeight="700">{g.score === null ? <Text as="span" color="orange.500" fontWeight="600">Vacía</Text> : formatScore(g.score)}</Td>
                            <Td {...tdProps}><SourceOfRecord value={g.source} /></Td>
                            {canEdit && (
                              <Td {...tdProps} textAlign="right">
                                <IconButton aria-label={`Editar nota de ${g.subject}`} icon={<FiEdit2 />} size="sm" variant="outline" borderRadius="8px" onClick={() => editGrade(g)} />
                              </Td>
                            )}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Section>

                <Section
                  title="Pagos"
                  action={canEdit && <Button size="sm" variant="outline" leftIcon={<FiPlus />} color="#574feb" onClick={() => editPayment(null)}>Agregar pago</Button>}
                >
                  <Box border="1px solid" borderColor="gray.200" borderRadius="10px" overflowX="auto">
                    <Table size="sm">
                      <Thead><Tr><Th {...thProps}>Fecha</Th><Th {...thProps}>Concepto</Th><Th {...thProps}>Método</Th><Th {...thProps} isNumeric>Valor</Th><Th {...thProps}>Fuente</Th>{canEdit && <Th {...thProps} />}</Tr></Thead>
                      <Tbody>
                        {s.payments.length === 0 && (
                          <Tr><Td colSpan={cols} {...tdProps} color="gray.500" py={4}>Sin pagos registrados.</Td></Tr>
                        )}
                        {s.payments.map((p) => (
                          <Tr key={p.id}>
                            <Td {...tdProps} whiteSpace="nowrap">{formatDate(p.date)}</Td>
                            <Td {...tdProps} fontFamily={p.concept ? 'mono' : undefined} fontSize={p.concept ? '12px' : undefined} color={p.concept ? undefined : 'gray.500'}>{p.concept || 'Sin concepto'}</Td>
                            <Td {...tdProps}>{p.method || '—'}</Td>
                            <Td {...tdProps} isNumeric>{formatMoney(p.amount)}</Td>
                            <Td {...tdProps}><SourceOfRecord value={p.source} /></Td>
                            {canEdit && (
                              <Td {...tdProps} textAlign="right">
                                <IconButton aria-label={`Editar pago del ${formatDate(p.date)}`} icon={<FiEdit2 />} size="sm" variant="outline" borderRadius="8px" onClick={() => editPayment(p)} />
                              </Td>
                            )}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Section>

                <Section title="Fila original del archivo">
                  <Text fontSize="12px" color="gray.500" mb={2}>
                    Solo lectura. Así venía en la pestaña «Estudiantes» de {SOURCE_LABEL[s.source]}, antes de corregir tildes y normalizar.
                  </Text>
                  <Box
                    as="pre" m={0} p={3} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="8px"
                    fontFamily="mono" fontSize="12px" lineHeight="1.6" overflowX="auto" whiteSpace="pre"
                  >
                    {s.originalData ? JSON.stringify(s.originalData, null, 2) : 'Sin datos originales.'}
                  </Box>
                </Section>

                {canEdit && (
                  <Flex justify="space-between" align="center" gap={3} pt={4} borderTopWidth="1px" borderColor="gray.100" wrap="wrap">
                    <Text fontSize="13px" color="gray.500" flex="1" minW="200px">
                      ¿Registro basura, bot o duplicado? Elimínalo de la Base histórica.
                    </Text>
                    <Button size="sm" variant="outline" colorScheme="red" leftIcon={<FiTrash2 />} onClick={deleteStudent}>
                      Eliminar estudiante
                    </Button>
                  </Flex>
                )}
              </Flex>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {modal && (
        <RecordModal
          isOpen={!!modal}
          onClose={() => setModal(null)}
          title={modal.title}
          fields={modal.fields}
          initialValues={modal.initialValues}
          onSubmit={modal.onSubmit}
          description={modal.description}
          submitLabel={modal.submitLabel}
          danger={modal.danger}
        />
      )}
    </>
  );
}

/** Lista de opciones que incluye el valor actual aunque no esté en el catálogo (datos viejos). */
function withCurrent(list = [], current) {
  if (!current || list.includes(current)) return list;
  return [current, ...list];
}
